<script lang="ts">
    import { _ } from "svelte-i18n"
    import MoreButton from "../../../widgets/MoreButton.svelte"
    import { topics } from "../../../../store"
    import { onMount } from "svelte"
    import { animateOnVisible } from "../../../../configs/config"
    import { parseSecondaryLink } from "../articleSlider/link-utils"

    let {
        slide,
        type,
        active,
        publication_date,
        splitted = false,
    }: {
        type: "primary" | "secondary"
        slide: Slide
        active: boolean
        publication_date?: string
        splitted?: boolean
    } = $props()

    if (typeof slide.topics_reference === "string")
        slide.topics_reference = [(slide.topics_reference as string).slice(3, -1)]

    let topics_t: string[] = $derived(
        Object.values($topics)
            .filter((t) => slide.topics_reference.includes(t.uuid))
            .map((t) => t.title)
    )
    let h3El = $state(null)
    let h1El = $state(null)
    let pEl = $state(null)
    let footerEl = $state(null)
    let headerEl = $state(null)

    function isPrimaryDisabled(value: unknown): boolean {
        if (typeof value === "boolean") return value
        if (typeof value === "number") return value === 1
        if (typeof value === "string") {
            const normalized = value.trim().toLowerCase()
            return normalized === "true" || normalized === "1" || normalized === "yes"
        }
        return false
    }

    const secondaryLinks = $derived(
        Array.isArray(slide.secondary_links) ? slide.secondary_links.filter((link) => link?.link && link?.text) : []
    )
    const hasSecondaryLinks = $derived(secondaryLinks.length > 0)
    const primaryLinkEnabled = $derived(
        Boolean(slide?.page_reference?.[0]) && !isPrimaryDisabled(slide.disable_primary)
    )

    function openLinkInNewTab(link: string) {
        return /^https?:\/\//i.test(link)
    }

    function resolveLinkTarget(link: { link: string; target?: string | null }) {
        if (link.target && link.target.length > 0) return link.target
        return openLinkInNewTab(link.link) ? "_blank" : "_self"
    }

    onMount(() => {
        const commonProps = {
            duration: 300,
            easing: "cubicBezier",
            opacity: [0, 1],
        }
        animateOnVisible(h1El, {
            translateY: ["30px", "0rem"],
            ...commonProps,
        })

        animateOnVisible([pEl, footerEl, headerEl], {
            delay: 600,
            duration: 300,
            ...commonProps,
        })
    })
</script>

<section class="{type}  slide-hero" class:splitted class:active>
    <div class="header" bind:this={headerEl}>
        {#if slide.tag}
            <small class="tag">{slide.tag}</small>
        {/if}
        {#each topics_t as topic}
            <small class="topic">
                <strong>{$_("general.topic")}</strong>
                {topic}
            </small>
        {/each}

        <small class="publication-date">
            {publication_date ? new Date(publication_date).toLocaleDateString("de-DE") : ""}
        </small>
    </div>
    <div class="main">
        <h1 bind:this={h1El}>{slide.title}</h1>
        {#if slide["subtitle"]}
            <h3 bind:this={h3El}>{slide["subtitle"]}</h3>
        {/if}
        {#if slide["description"]}
            <p bind:this={pEl}>{@html slide["description"].replace(/\n/g, "<br/>")}</p>
        {/if}
    </div>
    {#if hasSecondaryLinks || primaryLinkEnabled}
        <div class="footer" bind:this={footerEl}>
            <ul>
                {#if hasSecondaryLinks}
                    {#each secondaryLinks as link}
                        <li>
                            <MoreButton
                                size="big"
                                text={link.text}
                                path={parseSecondaryLink(link.link)}
                                type="white"
                                target={resolveLinkTarget(link)}
                            />
                        </li>
                    {/each}
                {/if}
                {#if primaryLinkEnabled}
                    <li>
                        <MoreButton
                            size="big"
                            text={$_("general.read-more")}
                            page_ref={slide.page_reference[0]}
                            type="white"
                        />
                    </li>
                {/if}
            </ul>
        </div>
    {/if}
</section>

<style lang="less">
    @import "../../../../css/variables.less";
    section {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        align-items: flex-start;
        &.primary {
            @media @desktop {
                width: 66%;
            }
        }
        &.splitted {
            width: 100%;
        }
        .header {
            display: flex;
            gap: 1.2rem;
            margin-bottom: 0.6rem;

            .topic,
            .publication-date {
                display: flex;
                align-items: center;
                text-transform: uppercase;
                color: var(--bright-100);
                font-size: 0.7rem;
            }
        }
        .main {
            h1 {
                color: var(--bright-100);
            }
            h3 {
                color: var(--bright-100);
            }
            p {
                color: var(--bright-100);
                margin-top: 0px;
                height: auto;
                max-height: 0px;
                overflow: hidden;
                transition: all 0.5s;
            }
        }

        &.active {
            .main p {
                margin-top: 0.6rem;
                height: auto;
                max-height: 400px;
            }
        }
        .footer {
            margin-top: 1.2rem;
            ul {
                display: flex;
                flex-wrap: wrap;
                gap: 0.6rem;
                margin: 0;
                padding: 0;
                list-style: none;
            }
        }
        &.secondary {
            .main {
                h1 {
                    display: none;
                }
                @media @max-mobile {
                    width: 100%;
                    overflow: hidden;
                    h3 {
                        font-size: 0.8rem;
                        line-height: 1rem;
                    }
                }
            }
            .footer {
                margin-top: 0px;
                max-height: 0px;
                overflow: hidden;
                transition:
                    max-height 0.3s ease,
                    padding-top 0.3s ease;
            }
            &.active {
                .footer {
                    padding-top: 1.2rem;
                    max-height: 10rem;
                }
            }
        }
    }
</style>
